import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { useReturnFocus } from 'hooks/useReturnFocus';
import type { TutorialVideo } from 'api/tutorialsApi';

interface TutorialPlayerDialogProps {
  video: TutorialVideo | null;
  onClose: () => void;
  onReload: () => void;
}

const TutorialPlayerDialog = ({ video, onClose, onReload }: TutorialPlayerDialogProps) => {
  const { t } = useTranslation();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const returnFocus = useReturnFocus();

  return (
    <Dialog open={video !== null} onOpenChange={(open) => !open && onClose()}>
      {/* Unmounted on close, not just hidden: that is what stops playback */}
      {video && (
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto"
          {...(!video.description && { 'aria-describedby': undefined })}
          {...returnFocus}
        >
          <DialogHeader>
            <DialogTitle className="break-words pr-6 leading-snug">{video.title}</DialogTitle>
            {video.description && <DialogDescription className="whitespace-pre-line break-words">{video.description}</DialogDescription>}
          </DialogHeader>
          {failedUrl === video.url ? (
            <div role="alert" className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-md bg-muted text-sm text-muted-foreground">
              <p>{t("Can't play this video")}</p>
              <Button variant="outline" size="sm" onClick={onReload}>
                {t('Reload')}
              </Button>
            </div>
          ) : (
            <video
              key={video.url}
              src={video.url}
              controls
              autoPlay
              className="aspect-video w-full rounded-md bg-black"
              onError={() => setFailedUrl(video.url)}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default TutorialPlayerDialog;
